import { TableCell, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';
import { BagSimple, Export } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { OrganisationAvatar } from '../../components/ImageAvatar';
import { ColoredPill, MuteText, ShadowBox } from '../../elements';
import { CommonUtility } from '../../utility/common';
import { ActionEditDots, ActionMenuItem, LoaderBar } from '../../components';
import { theme } from '../../utility';

const ActionEditTableRow = styled(TableRow)`
  position: relative;

  .action-container {
    margin: auto;
    background: transparent;
    right: 10px;
    bottom: 0;
  }

  &:hover {
    img.m-auto {
      opacity: 0.3;
    }
    .action-container {
      opacity: 1 !important;
    }
  }
`;

export function CompanyRow({ item, exportData }) {
  return (
    <ActionEditTableRow>
      <TableCell>
        <div className="d-flex align-items-center">
          <OrganisationAvatar
            image={item?.organization?.logo?.url}
            name={item.companyName}
          />
          <b className="ml-2">{item.companyName}</b>
        </div>
      </TableCell>
      <TableCell textAlign="center">
        <div className="d-flex justify-content-center">
          <ColoredPill color={theme.colors.green}>Active</ColoredPill>
        </div>
      </TableCell>
      <TableCell textAlign="center">
        {CommonUtility.numberWithCommas(item.mtdOrders)}
      </TableCell>
      <TableCell textAlign="center">
        {CommonUtility.numberWithCommas(item.mtdLabels)}
      </TableCell>
      <TableCell textAlign="center">
        {CommonUtility.currencyFormat(item.totalRate)}
      </TableCell>
      <TableCell textAlign="center">
        {CommonUtility.currencyFormat(item.totalCommission)}
        <ActionEditDots preventChangingBg on="hover" position="left center">
          <ActionMenuItem onClick={() => exportData(item.organization._id)}>
            <Export />
            Export
          </ActionMenuItem>
        </ActionEditDots>
      </TableCell>
    </ActionEditTableRow>
  );
}

const CardsContainer = styled.div`
  display: flex;
  row-gap: 10px;
  column-gap: 10px;
  margin-top: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const LargeFont = styled.span`
  font-size: 40px;
  font-weight: bold;
  margin-top: 15px;
`;

const AnalyticCard = styled(ShadowBox)`
  display: flex;
  justify-content: space-between;
  height: 150px;
  min-width: 280px;
  position: relative;
`;

const STATUS_METRICS = [
  { key: 'totalOrders', label: 'Total Orders' },
  { key: 'ordersShipped', label: 'Orders Shipped' },
  { key: 'pendingFulfillment', label: 'Pending Fulfillment' },
];

export function AnalyticCards({ data, loading }) {
  const metrics = useMemo(() => {
    if (!CommonUtility.isValidObject(data)) {
      return STATUS_METRICS.map(metric => ({ ...metric, value: 0 }));
    }

    const activeCompanies = data.activeCompanies || [];
    const fallbackTotalOrders = activeCompanies.reduce(
      (total, activeCompany) => total + (activeCompany.mtdOrders || 0),
      0,
    );
    const breakdown = data.orderStatusBreakdown || {};

    return STATUS_METRICS.map(metric => ({
      ...metric,
      value:
        metric.key === 'totalOrders'
          ? (breakdown.totalOrders ?? fallbackTotalOrders)
          : breakdown[metric.key] || 0,
    }));
  }, [data]);

  return (
    <CardsContainer>
      {metrics.map(metric => (
        <AnalyticCard key={metric.key}>
          {loading ? (
            <LoaderBar />
          ) : (
            <div className="d-flex flex-column justify-content-between ">
              <MuteText className="d-flex align-items-center">
                <BagSimple size={17} />
                <span className="mt-1 ml-2">{metric.label}</span>
              </MuteText>
              <div className="d-flex">
                <div className="d-flex flex-column">
                  <LargeFont className="mb-2">
                    {CommonUtility.numberWithCommas(metric.value)}
                  </LargeFont>
                  <div className="mt-4 pt-4" />
                </div>
              </div>
            </div>
          )}
        </AnalyticCard>
      ))}
    </CardsContainer>
  );
}
